import { cn } from '@workspace/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

const metricCardVariants = cva('p-4 rounded-xl border transition-colors', {
  variants: {
    variant: {
      default: 'border-border bg-input/50',
      highlight: 'border-[#f97316]/30 bg-[#f97316]/5',
      success: 'border-emerald-500/30 bg-emerald-500/5',
      warning: 'border-yellow-500/30 bg-yellow-500/5',
      danger: 'border-red-500/30 bg-red-500/5',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const metricIconVariants = cva('flex items-center gap-2 mb-2', {
  variants: {
    variant: {
      default: 'text-muted-foreground',
      highlight: 'text-[#f97316]',
      success: 'text-emerald-400',
      warning: 'text-yellow-400',
      danger: 'text-red-400',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const metricValueVariants = cva('text-2xl font-bold', {
  variants: {
    variant: {
      default: 'text-foreground',
      highlight: 'text-[#f97316]',
      success: 'text-emerald-400',
      warning: 'text-yellow-400',
      danger: 'text-red-400',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export interface MetricCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof metricCardVariants> {
  /** Icon element to display */
  icon?: React.ReactNode
  /** Metric label/name */
  label: string
  /** Metric value to display */
  value: string | number
  /** Optional subtext below value */
  subtitle?: string
}

export const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(
  ({ icon, label, value, subtitle, variant, className, ...props }, ref) => {
    return (
      <div ref={ref} className={cn(metricCardVariants({ variant }), className)} {...props}>
        <div className={cn(metricIconVariants({ variant }))}>
          {icon}
          <span className="text-sm">{label}</span>
        </div>
        <div className={cn(metricValueVariants({ variant }))}>{value}</div>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
    )
  }
)

MetricCard.displayName = 'MetricCard'
